import {
  applyDetent,
  blockFromDrag,
  dayBounds,
  DETENT_THRESHOLD_PX,
  detentBoundaries,
  moveBlock,
  moveGroup,
  ResizeEdge,
  resizeBlock,
  resolveGroupResize,
  type CreateOptions,
  type DetentResult,
  type GroupResizeResult,
  type IdSpan,
  type LiveTimeBlock,
  type Span,
} from '@wiztime/core';
import type { DisplayGroup } from './laneModel';

/**
 * Pure preview computation of a block lane (ui-redesign §3.2): the in-memory result of a create,
 * move or resize drag for a given pointer minute. Nothing here reads the DOM or the store; the
 * lane hook passes in the press data, the visible blocks and the measured scale.
 */

export const PreviewKind = { Create: 'create', Move: 'move', Resize: 'resize' } as const;
export type PreviewKind = (typeof PreviewKind)[keyof typeof PreviewKind];

/** The in-memory result of a drag in progress; the store is written once, on release. */
export interface LanePreview {
  readonly kind: PreviewKind;
  /** Whole preview span: the block, or the outer range of the merged group. */
  readonly span: Span;
  /** The single block being moved or resized; null while creating or acting on a whole group. */
  readonly blockId: string | null;
  /** Display group being moved or resized; null while creating. */
  readonly groupKey: string | null;
  /** Batched member spans of a group move or group resize (redesign §2.3); null otherwise. */
  readonly group: GroupResizeResult | null;
  /** Whole-move offset in minutes (0 for create and resize): drives `transform: translateY`. */
  readonly deltaMin: number;
  /** A resize handle currently sits on the other side of its anchor (redesign §2.2). */
  readonly flipped: boolean;
  /**
   * Personal date a week-view move drops the block on (redesign §2.8): the start and end
   * minutes stay as they are, so the span above is the original one, shown over the target
   * lane. Null while the pointer is on the block's own day, and always in the day view.
   */
  readonly targetDate: string | null;
  /** The dragged group is a draft held on its own day (§2.8): not-allowed cursor and tip. */
  readonly crossDenied: boolean;
}

/** What a press landed on: empty lane space, a block body, or a block edge. */
export const Gesture = { Empty: 'empty', Block: 'block', Resize: 'resize' } as const;
export type Gesture = (typeof Gesture)[keyof typeof Gesture];

/** The press data a preview depends on (a subset of the lane hook's pointer session). */
export interface PreviewPress {
  readonly gesture: Gesture;
  /** Wall minute under the pointer when it went down. */
  readonly anchorMin: number;
  readonly group: DisplayGroup | undefined;
  /** The block whose edge is resized, or the block that moves. */
  readonly member: LiveTimeBlock | undefined;
  readonly edge: ResizeEdge | undefined;
  /** Side of the resize anchor the handle is on; flips when the pointer crosses the anchor. */
  readonly side: ResizeEdge | null;
  /** Day column the pointer sits in (week view, §2.8); null on the source day or in the day view. */
  readonly crossDate: string | null;
  /** The dragged draft is held back from the day column under the pointer (§2.8). */
  readonly crossDenied: boolean;
}

/** The lane state a preview depends on besides the press. */
export interface PreviewContext {
  readonly options: CreateOptions;
  /** Visible blocks of the date (their boundaries attract a dragged edge). */
  readonly blocks: readonly LiveTimeBlock[];
  /** Screen pixels per minute of the lane; 0 while it is not measured. */
  readonly pxPerMin: number;
}

export interface PreviewResult {
  readonly preview: LanePreview | null;
  /** The resize handle's side after this pointer position (unchanged outside a single-block resize). */
  readonly side: ResizeEdge | null;
}

export function spanOf(block: { data: { startMin: number; endMin: number } }): Span {
  return { startMin: block.data.startMin, endMin: block.data.endMin };
}

export function sameSpan(a: Span | null, b: Span | null): boolean {
  return a === b || (a !== null && b !== null && a.startMin === b.startMin && a.endMin === b.endMin);
}

export function samePreview(a: LanePreview | null, b: LanePreview | null): boolean {
  if (a === null || b === null) return a === b;
  return (
    a.kind === b.kind &&
    a.blockId === b.blockId &&
    a.flipped === b.flipped &&
    a.targetDate === b.targetDate &&
    a.crossDenied === b.crossDenied &&
    sameSpan(a.span, b.span)
  );
}

/** Outer range of a set of member spans. */
export function groupSpan(members: readonly IdSpan[]): Span {
  return {
    startMin: Math.min(...members.map((member) => member.startMin)),
    endMin: Math.max(...members.map((member) => member.endMin)),
  };
}

/** The preview of a block a click or drag would create over `span`. */
export function createPreview(span: Span): LanePreview {
  return {
    kind: PreviewKind.Create,
    span,
    blockId: null,
    groupKey: null,
    group: null,
    deltaMin: 0,
    flipped: false,
    targetDate: null,
    crossDenied: false,
  };
}

/**
 * Neighbour detent (redesign §2.1.2, 2026-10b §3.6), for creating and resizing only — moving
 * never uses it. The dragged edge within `min(8px, one step's pixel height)` of another
 * block's boundary or a day bound stays on it — the bound keeps a fine step's nearby minutes
 * reachable; the edges of the dragged group itself do not attract their own handle. A hit
 * keeps the neighbour's exact boundary: the geometry then skips the step snapping.
 */
export function withDetent(
  rawMin: number,
  excludeIds: readonly string[],
  context: PreviewContext,
): DetentResult {
  if (context.pxPerMin <= 0) return { min: rawMin, hit: false };
  const others = context.blocks.filter((block) => !excludeIds.includes(block.id)).map(spanOf);
  const thresholdPx = Math.min(DETENT_THRESHOLD_PX, context.options.step * context.pxPerMin);
  return applyDetent(
    rawMin,
    detentBoundaries(others, dayBounds(context.options.dayStartMin)),
    context.pxPerMin,
    thresholdPx,
  );
}

/**
 * Resize of a merged group with several members (redesign §2.3): the outer edge follows the
 * pointer through `resolveGroupResize`, never flips, and the preview span is the remaining
 * group range [group start or boundary, boundary or group end]. `blockId` is the member whose
 * edge is dragged.
 */
export function groupResizePreview(
  group: DisplayGroup,
  blockId: string | null,
  edge: ResizeEdge,
  pointer: DetentResult,
  options: CreateOptions,
): LanePreview {
  const members: IdSpan[] = group.members.map((member) => ({ id: member.id, ...spanOf(member) }));
  const result = resolveGroupResize(members, edge, pointer.min, options, pointer.hit);
  const changed = new Map(result.changes.map((change) => [change.id, change]));
  const remaining = members
    .filter((member) => !result.deleteIds.includes(member.id))
    .map((member) => changed.get(member.id) ?? member);
  const span =
    remaining.length === 0 ? { startMin: group.startMin, endMin: group.endMin } : groupSpan(remaining);
  return {
    kind: PreviewKind.Resize,
    span,
    blockId,
    groupKey: group.key,
    group: result,
    deltaMin: 0,
    flipped: false,
    targetDate: null,
    crossDenied: false,
  };
}

/** The preview for the pointer at `currentMin` (null when the gesture has nothing to show yet). */
export function computePreview(
  press: PreviewPress,
  currentMin: number,
  context: PreviewContext,
): PreviewResult {
  const { options } = context;
  const unchanged = (preview: LanePreview | null): PreviewResult => ({ preview, side: press.side });
  if (press.gesture === Gesture.Empty) {
    const pointer = withDetent(currentMin, [], context);
    const span = blockFromDrag(
      press.anchorMin,
      pointer.min,
      options,
      context.blocks.map(spanOf),
      pointer.hit,
    );
    return unchanged(span === null ? null : createPreview(span));
  }
  const member = press.member;
  const group = press.group;
  if (member === undefined || group === undefined) return unchanged(null);
  const offset = currentMin - press.anchorMin;
  if (press.gesture === Gesture.Resize && press.edge !== undefined) {
    if (group.members.length > 1) {
      const edgeMin = press.edge === ResizeEdge.Start ? group.startMin : group.endMin;
      return unchanged(
        groupResizePreview(
          group,
          member.id,
          press.edge,
          withDetent(edgeMin + offset, group.memberIds, context),
          options,
        ),
      );
    }
    // Anchor = the opposite edge of the dragged one, fixed at press; the handle may flip
    // across it (redesign §2.2). `member.data` keeps the original span for the whole drag.
    const edgeMin = press.edge === ResizeEdge.Start ? member.data.startMin : member.data.endMin;
    const anchorMin = press.edge === ResizeEdge.Start ? member.data.endMin : member.data.startMin;
    const pointer = withDetent(edgeMin + offset, [member.id], context);
    const others = context.blocks.filter((block) => block.id !== member.id).map(spanOf);
    const result = resizeBlock(
      spanOf(member),
      anchorMin,
      pointer.min,
      options,
      press.side ?? press.edge,
      others,
      pointer.hit,
    );
    return {
      side: result.side,
      preview: {
        kind: PreviewKind.Resize,
        span: result.span,
        blockId: member.id,
        groupKey: group.key,
        group: null,
        deltaMin: 0,
        flipped: result.side !== press.edge,
        targetDate: null,
        crossDenied: false,
      },
    };
  }
  // Cross-day move (week view, §2.8): on another day the block keeps its start and end
  // minutes — the original span, previewed over the target lane and committed through
  // `moveBlocksToDate` in one undo step. Vertical movement resumes back on the source day.
  if (press.crossDate !== null) {
    return unchanged({
      kind: PreviewKind.Move,
      span: { startMin: group.startMin, endMin: group.endMin },
      blockId: group.members.length > 1 ? null : member.id,
      groupKey: group.key,
      group: null,
      deltaMin: 0,
      flipped: false,
      targetDate: press.crossDate,
      crossDenied: false,
    });
  }
  // Whole move, never with the detent (§2.1.2): a merged group moves as one (§2.3).
  if (group.members.length > 1) {
    const members: IdSpan[] = group.members.map((item) => ({ id: item.id, ...spanOf(item) }));
    const changes = moveGroup(members, offset, options);
    const span = changes.length === 0 ? groupSpan(members) : groupSpan(changes);
    return unchanged({
      kind: PreviewKind.Move,
      span,
      blockId: null,
      groupKey: group.key,
      group: { changes, deleteIds: [] },
      deltaMin: span.startMin - group.startMin,
      flipped: false,
      targetDate: null,
      crossDenied: press.crossDenied,
    });
  }
  const span = moveBlock(spanOf(member), member.data.startMin + offset, options);
  return unchanged({
    kind: PreviewKind.Move,
    span,
    blockId: member.id,
    groupKey: group.key,
    group: null,
    deltaMin: span.startMin - member.data.startMin,
    flipped: false,
    targetDate: null,
    crossDenied: press.crossDenied,
  });
}
